import { cn } from '@/lib/utils';
import { 
  MapPin, 
  Utensils, 
  Flame, 
  Edit, 
  Share2, 
  Star, 
  Compass, 
  Globe,
  LockKeyhole,
  PartyPopper
} from 'lucide-react';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  requirement: number;
  category: 'exploration' | 'engagement' | 'social' | 'special';
}

export const BADGES: Badge[] = [
  {
    id: 'welcome',
    name: 'Bienvenue',
    description: 'Complétez le tutoriel de bienvenue',
    icon: 'party',
    requirement: 1,
    category: 'special',
  },
  {
    id: 'explorer',
    name: 'Explorateur',
    description: 'Visitez 10 lieux différents',
    icon: 'compass',
    requirement: 10,
    category: 'exploration',
  },
  {
    id: 'gourmet',
    name: 'Gourmet Casher',
    description: 'Visitez 5 restaurants',
    icon: 'utensils',
    requirement: 5,
    category: 'exploration',
  },
  {
    id: 'faithful',
    name: 'Fidèle',
    description: 'Streak de 7 jours consécutifs',
    icon: 'flame',
    requirement: 7,
    category: 'engagement',
  },
  {
    id: 'contributor',
    name: 'Contributeur',
    description: 'Soumettez 3 suggestions',
    icon: 'edit',
    requirement: 3,
    category: 'engagement',
  },
  {
    id: 'ambassador',
    name: 'Ambassadeur',
    description: 'Partagez 5 lieux',
    icon: 'share',
    requirement: 5,
    category: 'social',
  },
  {
    id: 'critic',
    name: 'Critique',
    description: 'Laissez 3 avis',
    icon: 'star',
    requirement: 3,
    category: 'social',
  },
  {
    id: 'pioneer',
    name: 'Pionnier',
    description: 'Ajoutez un nouveau lieu',
    icon: 'mappin',
    requirement: 1,
    category: 'special',
  },
  {
    id: 'globetrotter',
    name: 'Globe-trotter',
    description: 'Visitez 3 villes différentes',
    icon: 'globe',
    requirement: 3,
    category: 'exploration',
  },
];

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  party: PartyPopper,
  compass: Compass,
  utensils: Utensils,
  flame: Flame,
  edit: Edit,
  share: Share2,
  star: Star,
  mappin: MapPin,
  globe: Globe,
};

const categoryColors: Record<string, string> = {
  exploration: 'from-blue-500 to-cyan-500',
  engagement: 'from-orange-500 to-yellow-500',
  social: 'from-pink-500 to-rose-500',
  special: 'from-purple-500 to-indigo-500',
};

interface BadgeCardProps {
  badge: Badge;
  isUnlocked: boolean;
  progress?: number;
  unlockedAt?: string;
  compact?: boolean;
  className?: string;
}

export const BadgeCard = ({
  badge,
  isUnlocked,
  progress = 0,
  unlockedAt,
  compact = false,
  className,
}: BadgeCardProps) => {
  const Icon = iconMap[badge.icon] || MapPin;
  const progressPercent = Math.min((progress / badge.requirement) * 100, 100);

  if (compact) {
    return (
      <div
        className={cn(
          "relative w-12 h-12 rounded-full flex items-center justify-center",
          isUnlocked
            ? `bg-gradient-to-br ${categoryColors[badge.category]}`
            : "bg-muted",
          className
        )}
      >
        {isUnlocked ? (
          <Icon className="w-6 h-6 text-white" />
        ) : (
          <>
            <Icon className="w-5 h-5 text-muted-foreground/50" />
            <LockKeyhole className="absolute w-3 h-3 text-muted-foreground bottom-0 right-0" />
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative rounded-xl p-4 border transition-all duration-300",
        isUnlocked
          ? "bg-card border-primary/20 shadow-md"
          : "bg-muted/30 border-border/50",
        className
      )}
    >
      <div className="flex items-start gap-3">
        {/* Badge Icon */}
        <div
          className={cn(
            "relative w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0",
            isUnlocked
              ? `bg-gradient-to-br ${categoryColors[badge.category]} shadow-lg`
              : "bg-muted"
          )}
        >
          {isUnlocked ? (
            <Icon className="w-7 h-7 text-white drop-shadow" />
          ) : (
            <>
              <Icon className="w-6 h-6 text-muted-foreground/40" />
              <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-xl">
                <LockKeyhole className="w-5 h-5 text-muted-foreground" />
              </div>
            </>
          )}
        </div>

        {/* Badge Info */}
        <div className="flex-1 min-w-0">
          <h4 className={cn(
            "font-semibold",
            isUnlocked ? "text-foreground" : "text-muted-foreground"
          )}>
            {badge.name}
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            {badge.description}
          </p>

          {/* Progress bar for locked badges */}
          {!isUnlocked && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>{progress}/{badge.requirement}</span>
                <span>{Math.round(progressPercent)}%</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    `bg-gradient-to-r ${categoryColors[badge.category]}`
                  )}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Unlock date for unlocked badges */}
          {isUnlocked && unlockedAt && (
            <p className="text-xs text-primary mt-1.5">
              ✨ Débloqué le {new Date(unlockedAt).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
